import { AddAPhoto, AutoAwesome } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../../hooks/use-is-mobile';

interface PhotoUploadHeaderProps {
  label: string;
  helper?: string;
  required: boolean;
  showAddButton: boolean;
  isProcessing: boolean;
  onAddClick: () => void;
  showAiButton?: boolean;
  onAddWithAiClick?: () => void;
  aiButtonDisabled?: boolean;
}

interface UploadButtonsProps {
  onAddClick: () => void;
  isProcessing: boolean;
  showAiButton: boolean;
  onAddWithAiClick?: () => void;
  aiButtonDisabled: boolean;
  isMobile: boolean;
}

function UploadButtons({
  onAddClick,
  isProcessing,
  showAiButton,
  onAddWithAiClick,
  aiButtonDisabled,
  isMobile,
}: UploadButtonsProps) {
  const { t } = useTranslation();
  const stack = isMobile && showAiButton;
  const plainLabel = showAiButton
    ? t('goodsReceiving.addPhotoPlain')
    : t('goodsReceiving.addPhoto');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: stack ? 'column' : 'row',
        gap: 1,
        width: stack ? '100%' : 'auto',
      }}
    >
      <Button
        variant="outlined"
        startIcon={<AddAPhoto />}
        onClick={onAddClick}
        size="small"
        disabled={isProcessing}
        fullWidth={stack}
        sx={{ minHeight: 48 }}
      >
        {plainLabel}
      </Button>
      {showAiButton && onAddWithAiClick && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AutoAwesome />}
          onClick={onAddWithAiClick}
          size="small"
          disabled={isProcessing || aiButtonDisabled}
          fullWidth={isMobile}
          sx={{ minHeight: 48 }}
        >
          {t('goodsReceiving.addPhotoWithAi')}
        </Button>
      )}
    </Box>
  );
}

export function PhotoUploadHeader({
  label,
  helper,
  required,
  showAddButton,
  isProcessing,
  onAddClick,
  showAiButton = false,
  onAddWithAiClick,
  aiButtonDisabled = false,
}: PhotoUploadHeaderProps) {
  const isMobile = useIsMobile();
  const stack = isMobile && showAiButton;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: stack ? 'column' : 'row',
        alignItems: stack ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: stack ? 1.5 : 1,
      }}
    >
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 'medium',
          }}
        >
          {label}
          {required && (
            <Typography component="span" color="error">
              {' '}
              *
            </Typography>
          )}
        </Typography>
        {helper && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
            }}
          >
            {helper}
          </Typography>
        )}
      </Box>
      {showAddButton && (
        <UploadButtons
          onAddClick={onAddClick}
          isProcessing={isProcessing}
          showAiButton={showAiButton}
          onAddWithAiClick={onAddWithAiClick}
          aiButtonDisabled={aiButtonDisabled}
          isMobile={isMobile}
        />
      )}
    </Box>
  );
}
